import * as XLSX from 'xlsx';
import { Empty, Flex } from 'antd';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { first, isEmpty } from 'lodash';
import MainCard from 'components/MainCard';
import { useTheme } from '@mui/material/styles';
import LynIcon from 'components/@extended/LynIcon';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { TanStackColumnFilter } from 'components/@extended/ColumnFilter';
import { TanStackColumnSorting } from 'components/@extended/SortingIconGroup';
import { SelectVisibleColumnV8 } from 'components/@extended/SelectVisibleColumn';
import { enhanceColumnsWithFiltering, generateExcelFileName } from 'utils/utils';
import { useGetSingleIdentityMutation } from 'store/features/process/processApi';
import { TranStackPagination } from 'components/LynDataTable/DataTablePagination';
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import tableDesign from '../../../../../themes/table';
import ExportButton from 'components/button/ExportButton';
import { use } from 'react';

const FILTER_STORAGE_KEY = 'table-column-filters';

const getInitialColumnFilters = () => {
	try {
		const saved = localStorage.getItem(FILTER_STORAGE_KEY);
		return saved ? JSON.parse(saved) : [];
	} catch {
		return [];
	}
};

export function IdentityFeaturedTable({
	data,
	setData,
	rowSelection,
	columns = [],
	processDetails,
	selectedTabName,
	handleSelectRow,
	setRowSelection,
	buttonVisibility,
	approveRejectHandler,
	activeDeactivateHandle,
	xlsxFileName = 'lynorg',
}) {
	const theme = useTheme();
	const [searchParams, setSearchParams] = useSearchParams();

	const pageSizeFromParams = searchParams.get('size');
	const pageIndexFromParams = searchParams.get('page');

	const initialPageSize = pageSizeFromParams ? Number(pageSizeFromParams) : 15;
	const initialPageIndex = pageIndexFromParams ? Number(pageIndexFromParams) : 0;

	const [selectedRow, setSelectedRow] = useState({});
	const [selectedIndex, setSelectedIndex] = useState();
	const [originalData, setOriginalData] = useState(() => [...data]);
	const [columnFilters, setColumnFilters] = useState(getInitialColumnFilters);

	const [GetSingleIdentity, { data: singleCaseData, error: singleCaseError, isSuccess: isSingleCaseSuccess, isError: isSingleCaseError }] =
		useGetSingleIdentityMutation();

	const table = useReactTable({
		data,
		columns: enhanceColumnsWithFiltering(columns),
		state: {
			pagination: {
				pageIndex: initialPageIndex,
				pageSize: initialPageSize,
			},
			columnFilters,
		},
		onColumnFiltersChange: (updater) => {
			const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
			setColumnFilters(newFilters);
			// Reset page index to 0

			// Update URL: set page to 0

			if (newFilters?.length > 0) {
				// table.setPageIndex(0);

				setSearchParams(
					(prev) => {
						const newParams = new URLSearchParams(prev);
						newParams.set('page', '0');
						return newParams;
					},
					{ replace: true }
				);

				// const newSearchParams = new URLSearchParams(window.location.search);
				// newSearchParams.set('page', '0');
				// window.history.replaceState(null, '', `${window.location.pathname}?${newSearchParams.toString()}`);
			}
		},
		initialState: {
			columnVisibility: visibleColumnFunc(columns),
			columnPinning: piningColumnFunc(columns),
		},
		// defaultColumn: {
		// 	cell: RowEditable,
		// },
		meta: {
			selectedRow,
			setSelectedRow,
			saveData: (rowIndex) => {
				const row = data?.[rowIndex];

				if (row) {
					setSelectedIndex(rowIndex);
					GetSingleIdentity({
						_id: row.id,
						operation: 'update-single',
						processCode: processDetails.processCode,
						suffixCode: '',
						data: row,
						version: row?.version,
					});
				}
			},
			revertData: (rowIndex) => {
				setData((old) => old.map((row, index) => (index === rowIndex ? originalData[rowIndex] : row)));
			},
			updateSingleCellData: (rowIndex, columnId, value) => {
				setData((old) =>
					old.map((row, index) => {
						if (index === rowIndex) {
							return {
								...old[rowIndex],
								[columnId]: value,
							};
						}
						return row;
					})
				);
			},
			submitOperation: (id, operation, rowIndex) => {
				setSelectedIndex(rowIndex);
				GetSingleIdentity({
					_id: id,
					operation,
					processCode: processDetails.processCode,
					suffixCode: '',
				});
			},
		},
		filterFns: {
			// Standard text filter for better text matching
			textFilter: (row, columnId, filterValue) => {
				if (!filterValue) return true;
				const cellValue = String(row.getValue(columnId) || '').toLowerCase();
				const searchValue = String(filterValue).toLowerCase();
				return cellValue.includes(searchValue);
			},
		},
		globalFilterFn: 'textFilter',

		enableRowSelection: true,
		// onRowSelectionChange: setSelectedItems,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
	});

	const dataExport = useCallback(() => {
		const headerArr = table
			?.getVisibleLeafColumns()
			?.map((item) => item.id)
			?.filter((item) => item !== 'Selection' && item !== 'activationStatus' && item !== 'Action');

		// Extracting Label of the table
		const mainColumn = columns
			?.filter((item) => item.id !== 'Selection' && item.key !== 'activationStatus' && item.key !== 'Action')
			.reduce((acc, col) => {
				acc[col.key] = col.header;
				return acc;
			}, {});

		const Header = headerArr.map((item) => {
			if (mainColumn?.[item]) {
				return mainColumn?.[item];
			} else {
				return item;
			}
		});

		const rows = table.getFilteredRowModel().rows;
		const rowsArr = rows.map((row) => {
			const rowObj = {};
			headerArr.forEach((colId) => {
				rowObj[colId] = row.getValue(colId);
			});
			return rowObj;
		});

		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet([]);
		XLSX.utils.sheet_add_aoa(ws, [Header]);
		XLSX.utils.sheet_add_json(ws, rowsArr, { origin: 'A2', skipHeader: true });
		XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
		XLSX.writeFile(wb, `${generateExcelFileName(xlsxFileName)}.xlsx`);
	}, []);

	useEffect(() => {
		localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
	}, [columnFilters]);

	// useEffect(() => {
	// 	console.log(table?.getSelectedRowModel()?.flatRows, 'table?.getSelectedRowModel()?.flatRows');
	// 	setRowSelection(table?.getSelectedRowModel()?.flatRows);
	// }, [table?.getSelectedRowModel()?.flatRows]);

	useEffect(() => {
		const selectedRows = table.getSelectedRowModel().flatRows;

		if (selectedRows.length > 0) {
			setRowSelection(selectedRows);
		} else {
			setRowSelection([]);
		}
	}, [table.getState().rowSelection]);

	useEffect(() => {
		if (rowSelection?.length <= 0) {
			table.resetRowSelection();
		}
	}, [rowSelection]);

	useEffect(() => {
		if (isSingleCaseSuccess) {
			// set new version form api for new update
			if (!isEmpty(singleCaseData?.data)) {
				if (singleCaseData?.recordStatus === 'deleted') {
					setData((prev) => {
						return prev.filter((item) => item.id !== singleCaseData?._id);
					});
					setOriginalData((prev) => {
						return prev.filter((item) => item.id !== singleCaseData?._id);
					});
				} else {
					setData((old) =>
						old.map((row, index) => {
							if (index === selectedIndex) {
								return {
									key: index,
									recordId: singleCaseData._id,
									id: singleCaseData._id,
									...singleCaseData?.data,
									parkedData: singleCaseData?.parkedData,
									recordStatus: singleCaseData.recordStatus,
									version: singleCaseData?.version,
									parkAction: singleCaseData?.parkAction,
								};
							}
							return row;
						})
					);

					setOriginalData((old) =>
						old.map((row, index) => {
							if (index === selectedIndex) {
								return {
									key: index,
									recordId: singleCaseData._id,
									id: singleCaseData._id,
									...singleCaseData?.data,
									parkedData: singleCaseData?.parkedData,
									recordStatus: singleCaseData.recordStatus,
									version: singleCaseData?.version,
									parkAction: singleCaseData?.parkAction,
								};
							}
							return row;
						})
					);
				}
			}

			toast.success('Success! Action completed ', toastConfig);
		}

		if (isSingleCaseError) {
			if (singleCaseError?.data?.code === '501') {
				toast.error(singleCaseError?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
			setData((old) => old.map((row, index) => (index === selectedIndex ? originalData[selectedIndex] : row)));
		}
	}, [isSingleCaseSuccess, isSingleCaseError]);

	return (
		<>
			<Box content="div" sx={{ display: 'flex', justifyContent: 'space-between' }}>
				<SelectVisibleColumnV8 table={table} allColumns={table?.getAllColumns()} visibleColumns={[]} />
				<Flex align="center" gap=".5rem">
					{(() => {
						const useNewLogic = buttonVisibility !== undefined;

						const showActivate = useNewLogic ? buttonVisibility.activate : !['Active', 'Worklist', 'Drafts']?.includes(selectedTabName);

						const showDeactivate = useNewLogic
							? buttonVisibility.deactivate
							: !['Inactive', 'Worklist', 'Drafts']?.includes(selectedTabName);

						const showSubmit = useNewLogic ? buttonVisibility.submit : ['Drafts']?.includes(selectedTabName);

						const showApproveReject = useNewLogic
							? buttonVisibility.approveReject
							: !['Active', 'NEW_INACTIVE', 'Inactive', 'Drafts', 'All']?.includes(selectedTabName);

						if (Object.keys(rowSelection)?.length === 0) {
							return null;
						}

						return (
							<>
								<div>
									<b style={{ fontSize: '14px' }}>{Object.keys(rowSelection)?.length}</b>{' '}
									{Object.keys(rowSelection)?.length > 1 ? 'rows' : 'row'} selected
								</div>

								{showActivate && (
									<Tooltip title={Object.keys(rowSelection)?.length > 1 ? 'Activate all' : 'Activate'}>
										<Button
											type="primary"
											className="animated-button"
											style={{
												backgroundColor: theme.palette.lynTheme.success,
												color: 'white',
												padding: '8px 8px',
												fontSize: '14px',
												lineHeight: '18px',
											}}
											onClick={(e) => {
												e.stopPropagation();
												activeDeactivateHandle('Inactive');
											}}
										>
											{Object.keys(rowSelection)?.length > 1 ? 'Activate all' : 'Activate'}
										</Button>
									</Tooltip>
								)}

								{showDeactivate && (
									<Tooltip title={Object.keys(rowSelection)?.length > 1 ? 'Deactivate all' : 'Deactivate'}>
										<Button
											type="primary"
											className="animated-button"
											onClick={(e) => {
												e.stopPropagation();
												activeDeactivateHandle('Active');
											}}
											sx={{
												backgroundColor: '#B00020',
												color: '#fff',
												'&:hover': { backgroundColor: '#B00020' },
												padding: '8px 8px',
												fontSize: '14px',
												lineHeight: '18px',
											}}
										>
											{Object.keys(rowSelection)?.length > 1 ? 'Deactivate all' : 'Deactivate'}
										</Button>
									</Tooltip>
								)}

								{showSubmit && (
									<Tooltip title={Object.keys(rowSelection)?.length > 1 ? 'Submit all drafts' : 'Submit draft'}>
										<Button
											type="primary"
											className="animated-button"
											sx={{
												backgroundColor: '#03396c',
												color: '#fff',
												'&:hover': { backgroundColor: '#03396c' },
												padding: '8px 8px',
												fontSize: '14px',
												lineHeight: '18px',
											}}
											onClick={(e) => {
												e.stopPropagation();

												activeDeactivateHandle('submit');
											}}
										>
											{Object.keys(rowSelection)?.length > 1 ? 'Submit all' : 'Submit'}
										</Button>
									</Tooltip>
								)}

								{showApproveReject && (
									<>
										<Tooltip title="Approve">
											<Button
												type="primary"
												variant="contained"
												sx={{
													color: '#fff',
													backgroundColor: 'rgb(3, 57, 108)',

													'&:hover': { backgroundColor: 'rgb(3, 57, 108)' },
													padding: '8px 8px',
													fontSize: '14px',
													lineHeight: '18px',
												}}
												onClick={(e) => {
													e.stopPropagation();

													if (typeof approveRejectHandler === 'function') {
														const data = table.getSelectedRowModel().flatRows.map((item) => item.original);
														approveRejectHandler(data, 'Approve');
													}
												}}
											>
												Approve
											</Button>
										</Tooltip>

										<Tooltip title="Reject">
											<Button
												type="primary"
												variant="outlined"
												sx={{
													backgroundColor: '#B00020',
													color: '#fff',
													'&:hover': { backgroundColor: '#B00020' },
													padding: '8px 8px',
													fontSize: '14px',
													lineHeight: '18px',
												}}
												onClick={(e) => {
													e.stopPropagation();

													if (typeof approveRejectHandler === 'function') {
														const data = table.getSelectedRowModel().flatRows.map((item) => item.original);
														approveRejectHandler(data, 'Reject');
													}
												}}
											>
												Reject
											</Button>
										</Tooltip>
									</>
								)}
							</>
						);
					})()}

					{/* <Button
						disableRipple
						color="secondary"
						onClick={dataExport}
						aria-haspopup="true"
						aria-label="open profile"
						sx={{ padding: '8px 12px', color: 'text.primary', bgcolor: 'common.white', border: `1px solid ${theme.palette.border}` }}
					>
						<Stack direction="row" gap={1}>
							<LynIcon folder="icons" fileName={'export'} format="svg" sx={{ marginRight: '4px' }} />{' '}
							<Typography style={{ mt: '12px' }}>Export</Typography>
						</Stack>
					</Button> */}

					<ExportButton handleDataExport={dataExport} />
				</Flex>
			</Box>

			<MainCard
				content={false}
				sx={{
					mt: 2,
					width: '100%',
					overflow: 'hidden',
					borderColor: tableDesign.borderColor.primary,
					borderRadius: tableDesign.borderRadius.primary,
					border: 'none',
				}}
			>
				<TableContainer
					sx={{
						width: '100%',
						display: 'block',
						overflowX: 'auto',
						position: 'relative',
						maxHeight: `calc(100svh - 350px)`,

						'& td, & th': { whiteSpace: 'nowrap' },
					}}
				>
					<Table
						stickyHeader
						sx={{
							tableLayout: 'fixed',

							'& td, th': {
								borderRight: 1,
								borderColor: tableDesign.borderColor.primary,
							},

							'& th': {
								'&:first-of-type': { borderLeft: `1px solid ${tableDesign.borderColor.primary}` },
							},
						}}
					>
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id} sx={{ height: '40px' }}>
									{headerGroup.headers.map((header) => (
										<TableCell
											key={header.id}
											sx={{
												...getCommonPinningStyles(header.column, 'header'),

												color: tableDesign.color.primary,
												padding: tableDesign.padding.default,
												backgroundColor: tableDesign.background.primary,

												...(header.column.columnDef?.meta?.style ?? {}),
											}}
										>
											{header.id === 'Selection' ? (
												header.isPlaceholder ? null : (
													flexRender(header.column.columnDef.header, header.getContext())
												)
											) : (
												<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
													{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

													<TanStackColumnSorting header={header} />
												</Box>
											)}
										</TableCell>
									))}
								</TableRow>
							))}
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableCell
											key={header.id}
											sx={{
												...getCommonPinningStyles(header.column, 'header'),

												top: '40px',
												position: 'sticky',
												padding: tableDesign.padding.secondary,
												backgroundColor: tableDesign.background.primary,

												'& .ant-input-affix-wrapper': { border: '1px solid #ced4da !important' },
											}}
										>
											{header.column.getCanFilter() ? <TanStackColumnFilter column={header.column} /> : null}
										</TableCell>
									))}
								</TableRow>
							))}
						</TableHead>

						<TableBody
							sx={{
								'& tr:nth-of-type(even)': {
									backgroundColor: `${tableDesign.tableRowBackgroundColor.primary} !important`,
								},
							}}
						>
							{table.getRowModel().rows?.length > 0 ? (
								table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										sx={{ cursor: 'pointer' }}
										onClick={(e) => {
											e.stopPropagation();

											handleSelectRow(row?.original);
										}}
									>
										{row.getVisibleCells().map((cell) => {
											return (
												<TableCell
													key={cell.id}
													title={cell.getValue()}
													sx={{
														...getCommonPinningStyles(cell.column, 'body'),
														overflow: 'hidden',
														textOverflow: 'ellipsis',
														borderBottom: '1px solid #E0E0E0',
														padding: tableDesign.padding.primary,
														fontSize: tableDesign.fontSize.primary,
														fontWeight: tableDesign.fontWeight.primary,
														...(cell.column.columnDef?.meta?.style ?? {}),

														'&:first-of-type': { borderLeft: '1px solid', borderColor: tableDesign.borderColor.primary },
													}}
												>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											);
										})}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={columns?.length} sx={{ border: '1px solid', borderColor: tableDesign.borderColor.primary }}>
										<Box
											component="div"
											sx={{
												width: '100%',
												display: 'flex',
												height: '350px',
												alignItems: 'center',
												flexDirection: 'column',
												justifyContent: 'center',
											}}
										>
											<Empty />
										</Box>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<Box
					sx={{
						width: '100%',
						backgroundColor: tableDesign.background.primary,
					}}
				>
					<TranStackPagination
						table={table}
						sx={{
							'&.Mui-disabled': { color: 'red' },
							'&.MuiTablePagination-root': { color: tableDesign.color.primary },
							'.MuiTablePagination-selectIcon': { color: tableDesign.color.primary },
						}}
					/>
				</Box>
			</MainCard>
		</>
	);
}

export const getCommonPinningStyles = (column, type) => {
	const isPinned = column.getIsPinned();

	const styles = {
		width: column.getSize(),
	};

	if (column.id === 'Selection' && type === 'body') styles['padding'] = 0;
	if (isPinned) styles['zIndex'] = type === 'header' ? 3 : 1;
	if (isPinned) styles['position'] = 'sticky';
	if (isPinned === 'left') styles['left'] = `${column.getStart('left')}px`;
	if (isPinned === 'right') styles['right'] = `${column.getAfter('right')}px`;
	if (type === 'body') styles['background'] = '#ffffff';

	return styles;
};

const visibleColumnFunc = (columns) => {
	const visibleColumn = {};
	columns?.forEach((column) => {
		if (column?.columnSerialTable === null) {
			visibleColumn[column?.accessorKey] = false;
		} else {
			visibleColumn[column?.accessorKey] = true;
		}
	});

	return visibleColumn;
};

const piningColumnFunc = (columns) => {
	const pinedColumns = {};
	const firstColumnId = first(columns)?.accessorKey ?? first(columns)?.id;

	pinedColumns['left'] = [firstColumnId];
	pinedColumns['right'] = ['edit'];

	return pinedColumns;
};
